import { ReactNode, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import useIsMounted from 'flow/hooks/useIsMounted';
import { useModal } from 'flow/contexts/ModalProvider';

import BaseModal, { modalSizes } from 'flow/components/ui-components/BaseModal';
import Button from 'flow/components/ui-components/Button';
import Loading from 'flow/components/ui-components/Loading';
import MeetingAccessDropdown from './MeetingAccessDropdown';

import { setSnackbar } from 'flow/actions/app-actions';
import {
    getConferenceLink,
    hasMediaPermissions,
} from 'flow/utils/meeting-utils';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import {
    selectLobbyMeetingAccess,
    selectLobbyNumber,
    selectLobbyPin,
} from 'flow/selectors/meeting-selectors';
import { selectNameInApp } from 'flow/selectors/selectors';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';
import { joinMeetingMuted } from 'flow/actions/meeting-actions';
import { updateMeetingAccess } from 'flow/actions/meeting-lobby-actions';

interface InfoRowProps {
    label: string;
    value: ReactNode;
}

const InfoRow = ({ label, value }: InfoRowProps) => (
    <div className="flex flex-col">
        <span className="text-xs text-gray-500">{label}</span>
        <span className="truncate text-base font-bold text-gray-800">
            {value || '—'}
        </span>
    </div>
);

const actionButtonClass = 'min-w-0 flex-1 text-center';
const secondaryButtonClass = `${actionButtonClass} border border-solid border-gray-300`;

interface Props {
    roomName?: string;
    meetingCode?: string;
    chatSessionKey?: string;
    // The value just chosen (and persisted) in CreateMeetingRoomModal, for
    // the brief window before the lobby's own fetched copy reflects it.
    meetingAccess?: string;
}

const RoomCreatedModal = ({
    roomName,
    meetingCode,
    chatSessionKey,
    meetingAccess: initialMeetingAccess,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();
    const history = useHistory();

    const isMounted = useIsMounted();

    const pin = useAppSelector(selectLobbyPin);
    const number = useAppSelector(selectLobbyNumber);
    const countryCode = useAppSelector(selectUserCountryCode);
    const nameInApp = useAppSelector(selectNameInApp);
    const lobbyMeetingAccess = useAppSelector(selectLobbyMeetingAccess);
    const meetingAccess = lobbyMeetingAccess ?? initialMeetingAccess;

    const joinLink = getConferenceLink(meetingCode, countryCode);

    const [isStarting, setIsStarting] = useState(false);

    const handleShare = async () => {
        const lines = [
            i18next.t('videoconference.email.body.invitation'),
            `${i18next.t('videoconference.email.body.join_video')} ${joinLink}`,
        ];

        if (pin) {
            lines.push(
                `${i18next.t('videoconference.email.body.join_phone')} ${number}, ${pin}`,
            );
        }

        const body = lines.join('\n\n');

        try {
            await navigator.clipboard.writeText(body);
            dispatch(
                setSnackbar({ i18nextMessage: 'snackbar.copied_to_clipboard' }),
            );
        } catch {
            dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
        }
    };

    const navigateToRoom = () => {
        if (!chatSessionKey) {
            dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            return;
        }
        history.push(
            generatePath(routes['messages.id'], { id: chatSessionKey }),
        );
        closeModal();
    };

    const navigateToMeeting = () => {
        if (chatSessionKey) {
            history.push(
                generatePath(routes['messages.id.lobby'], {
                    id: chatSessionKey,
                }),
                { from: routes['messages.meetings'] },
            );
            closeModal();
        }
    };

    const handleStartMeeting = async () => {
        if (!meetingCode || !chatSessionKey) {
            dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            return;
        }

        setIsStarting(true);
        try {
            const permissionsGranted = await hasMediaPermissions();
            if (permissionsGranted && nameInApp) {
                await dispatch(joinMeetingMuted(nameInApp, meetingCode));
            }

            if (isMounted.current) {
                navigateToMeeting();
            }
        } catch {
            if (isMounted.current) {
                setIsStarting(false);
                dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            }
        }
    };

    return (
        <BaseModal
            open={true}
            header={
                <Trans
                    i18n={i18next}
                    i18nKey="meetings.room_created.title"
                    components={{ b: <strong /> }}
                    values={{ name: roomName ?? '' }}
                />
            }
            modalSize={modalSizes.MEDIUM}
            disableActions={true}
            disableHeaderForcedLowerCase={true}
        >
            <div className="flex flex-col gap-y-6 pb-6">
                <div className="flex flex-col gap-y-3 rounded-sm border border-gray-200 p-3">
                    <InfoRow
                        label={i18next.t('meetings.room_created.link_label')}
                        value={joinLink}
                    />
                    <InfoRow
                        label={i18next.t('meetings.room_created.phone_label')}
                        value={number}
                    />
                    <InfoRow
                        label={i18next.t('meetings.room_created.pin_label')}
                        value={pin}
                    />
                </div>

                <MeetingAccessDropdown
                    value={meetingAccess}
                    onChange={(newAccess) => {
                        if (chatSessionKey) {
                            dispatch(
                                updateMeetingAccess(chatSessionKey, newAccess),
                            );
                        }
                    }}
                />

                <div className="flex gap-x-3">
                    <Button
                        color="secondary"
                        className={secondaryButtonClass}
                        disabled={isStarting}
                        onClick={handleShare}
                    >
                        {i18next.t('meetings.room_created.share_button')}
                    </Button>
                    <Button
                        color="secondary"
                        className={secondaryButtonClass}
                        disabled={isStarting}
                        onClick={handleStartMeeting}
                        aria-label={i18next.t(
                            'meetings.room_created.start_button',
                        )}
                        aria-busy={isStarting}
                    >
                        {isStarting ? (
                            <Loading size={16} disableTimeout={true} />
                        ) : (
                            i18next.t('meetings.room_created.start_button')
                        )}
                    </Button>
                    <Button
                        color="primary"
                        className={actionButtonClass}
                        disabled={isStarting}
                        onClick={navigateToRoom}
                    >
                        {i18next.t('meetings.room_created.go_button')}
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
};

export default RoomCreatedModal;
